import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMemoizedFn } from "ahooks";
import { hasTransferFiles } from "./sender.attachmentUtils";

export type SenderFileDragPhase = "idle" | "page" | "over";
const VIEWPORT_EDGE_THRESHOLD_PX = 2;
const HARD_LEAVE_COOLDOWN_MS = 260;

type UseSenderFileDropInteractionParams = {
  enabled: boolean;
  dropZoneRef: React.RefObject<HTMLDivElement | null>;
  onDropToUpload: (e: React.DragEvent<HTMLDivElement>) => void | Promise<void>;
};

type DropZoneDragHandlers = Pick<
  React.HTMLAttributes<HTMLDivElement>,
  "onDragEnter" | "onDragOver" | "onDragLeave" | "onDrop"
>;

function isLeavingViewport(e: DragEvent): boolean {
  return (
    e.clientX <= 0 ||
    e.clientY <= 0 ||
    e.clientX >= window.innerWidth ||
    e.clientY >= window.innerHeight
  );
}

function isAtViewportEdge(e: DragEvent): boolean {
  return (
    e.clientX <= VIEWPORT_EDGE_THRESHOLD_PX ||
    e.clientY <= VIEWPORT_EDGE_THRESHOLD_PX ||
    e.clientX >= window.innerWidth - VIEWPORT_EDGE_THRESHOLD_PX ||
    e.clientY >= window.innerHeight - VIEWPORT_EDGE_THRESHOLD_PX
  );
}

function isPointInsideDropZone(
  dropZone: HTMLDivElement | null,
  x: number,
  y: number,
): boolean {
  if (!dropZone) {
    return false;
  }
  const rect = dropZone.getBoundingClientRect();
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

export function useSenderFileDropInteraction({
  enabled,
  dropZoneRef,
  onDropToUpload,
}: UseSenderFileDropInteractionParams) {
  const [fileDragPhase, setFileDragPhase] =
    useState<SenderFileDragPhase>("idle");
  const fileDragPhaseRef = useRef<SenderFileDragPhase>("idle");
  const hardLeaveUntilRef = useRef(0);

  const clearFileDragState = useCallback(() => {
    setFileDragPhase("idle");
  }, []);

  const handleDropToUpload = useMemoizedFn(onDropToUpload);

  useEffect(() => {
    fileDragPhaseRef.current = fileDragPhase;
  }, [fileDragPhase]);

  const markHardLeave = useCallback(() => {
    hardLeaveUntilRef.current = Date.now() + HARD_LEAVE_COOLDOWN_MS;
    clearFileDragState();
  }, [clearFileDragState]);

  useEffect(() => {
    const handleWindowDragEnterOrOver = (e: DragEvent) => {
      if (!hasTransferFiles(e.dataTransfer)) {
        return;
      }
      if (!enabled) {
        return;
      }
      if (Date.now() < hardLeaveUntilRef.current) {
        return;
      }
      const dropZone = dropZoneRef.current;
      const isInsideDropZone = isPointInsideDropZone(
        dropZone,
        e.clientX,
        e.clientY,
      );
      if (
        fileDragPhaseRef.current !== "idle" &&
        !isInsideDropZone &&
        isAtViewportEdge(e)
      ) {
        markHardLeave();
        return;
      }
      e.preventDefault();
      setFileDragPhase(isInsideDropZone ? "over" : "page");
    };

    const handleWindowDragLeave = (e: DragEvent) => {
      if (!hasTransferFiles(e.dataTransfer)) {
        return;
      }
      if (!enabled) {
        return;
      }
      if (!e.relatedTarget || isLeavingViewport(e)) {
        markHardLeave();
      }
    };

    const handleWindowDragEnd = () => {
      markHardLeave();
    };

    const handleWindowBlur = () => {
      markHardLeave();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        markHardLeave();
      }
    };

    const handlePointerLeavePage = (event: MouseEvent | PointerEvent) => {
      if (event.relatedTarget) {
        return;
      }
      markHardLeave();
    };

    const handleWindowDrop = (e: DragEvent) => {
      if (!hasTransferFiles(e.dataTransfer)) {
        return;
      }
      e.preventDefault();
      if (!enabled) {
        markHardLeave();
        return;
      }

      const dropZone = dropZoneRef.current;
      if (dropZone && e.target instanceof Node && dropZone.contains(e.target)) {
        markHardLeave();
        return;
      }

      markHardLeave();
    };

    window.addEventListener("dragenter", handleWindowDragEnterOrOver, true);
    window.addEventListener("dragover", handleWindowDragEnterOrOver, true);
    window.addEventListener("dragleave", handleWindowDragLeave, true);
    window.addEventListener("dragend", handleWindowDragEnd, true);
    window.addEventListener("drop", handleWindowDrop, true);
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("mouseout", handlePointerLeavePage);
    window.addEventListener("pointerleave", handlePointerLeavePage);
    document.documentElement.addEventListener(
      "mouseleave",
      handlePointerLeavePage,
    );
    document.addEventListener("visibilitychange", handleVisibilityChange);

    if (!enabled) {
      clearFileDragState();
    }

    return () => {
      window.removeEventListener(
        "dragenter",
        handleWindowDragEnterOrOver,
        true,
      );
      window.removeEventListener("dragover", handleWindowDragEnterOrOver, true);
      window.removeEventListener("dragleave", handleWindowDragLeave, true);
      window.removeEventListener("dragend", handleWindowDragEnd, true);
      window.removeEventListener("drop", handleWindowDrop, true);
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("mouseout", handlePointerLeavePage);
      window.removeEventListener("pointerleave", handlePointerLeavePage);
      document.documentElement.removeEventListener(
        "mouseleave",
        handlePointerLeavePage,
      );
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [clearFileDragState, dropZoneRef, enabled, markHardLeave]);

  const dropZoneDragHandlers = useMemo<DropZoneDragHandlers>(() => {
    if (!enabled) {
      return {};
    }

    return {
      onDragEnter: (e) => {
        if (!hasTransferFiles(e.dataTransfer)) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        setFileDragPhase("over");
      },
      onDragOver: (e) => {
        if (!hasTransferFiles(e.dataTransfer)) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "copy";
        setFileDragPhase("over");
      },
      onDragLeave: (e) => {
        if (!hasTransferFiles(e.dataTransfer)) {
          return;
        }
        if (e.currentTarget.contains(e.relatedTarget as Node)) {
          return;
        }
        if (
          !e.relatedTarget ||
          e.clientX <= 0 ||
          e.clientY <= 0 ||
          e.clientX >= window.innerWidth ||
          e.clientY >= window.innerHeight ||
          isAtViewportEdge(e.nativeEvent)
        ) {
          markHardLeave();
          return;
        }
        setFileDragPhase("page");
      },
      onDrop: (e) => {
        if (!hasTransferFiles(e.dataTransfer)) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        markHardLeave();
        void handleDropToUpload(e);
      },
    };
  }, [enabled, handleDropToUpload, markHardLeave]);

  return {
    fileDragPhase,
    clearFileDragState,
    dropZoneDragHandlers,
  };
}
